<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { useAuthStore } from '../stores/auth';
import { useTokensStore } from '../stores/tokens';
import { formatDate, formatRelativeDay, isExpired } from '../utils/date';

const authStore = useAuthStore();
const tokensStore = useTokensStore();

const user = computed(() => authStore.user);
const isAdmin = computed(() => authStore.isAdmin);
const isDeveloper = computed(() => authStore.isDeveloper);
const organizationSlugs = computed(() => authStore.organizationSlugs);
const hasOrganizations = computed(() => organizationSlugs.value.length > 0);

// Token creation form
const showCreateForm = ref(false);
const tokenName = ref('');
const tokenExpiry = ref<string>('30');

const expiryOptions = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
  { value: '', label: 'Never' },
];

// Active tokens (not revoked)
const activeTokens = computed(() =>
  tokensStore.tokens.filter((t) => !t.revokedAt)
);

function getRoleBadgeClass(role: string): string {
  switch (role) {
    case 'admin':
      return 'role-admin';
    case 'developer':
      return 'role-developer';
    case 'readonly':
      return 'role-readonly';
    default:
      return 'role-default';
  }
}

async function handleCreateToken() {
  if (!tokenName.value.trim()) return;

  const expiresInDays = tokenExpiry.value ? parseInt(tokenExpiry.value, 10) : null;
  await tokensStore.createToken({
    name: tokenName.value.trim(),
    expiresInDays,
  });

  // Reset form but keep it open to show the token
  tokenName.value = '';
  tokenExpiry.value = '30';
}

async function handleRevokeToken(tokenId: string) {
  if (confirm('Are you sure you want to revoke this token? This action cannot be undone.')) {
    await tokensStore.revokeToken(tokenId);
  }
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Fallback for older browsers
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
}

function dismissNewToken() {
  tokensStore.clearNewToken();
  showCreateForm.value = false;
}

onMounted(() => {
  tokensStore.fetchTokens();
});
</script>

<template>
  <div class="profile-view">
    <div class="header">
      <h1>Profile</h1>
    </div>

    <div v-if="!user" class="loading">
      Loading...
    </div>

    <div v-else class="profile-content">
      <div class="profile-card">
        <div class="avatar">
          <span class="avatar-initial">{{ (user.name || user.preferredUsername || 'U')[0].toUpperCase() }}</span>
        </div>

        <div class="profile-details">
          <div class="detail-row">
            <span class="label">Name</span>
            <span class="value">{{ user.name || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Username</span>
            <span class="value">{{ user.preferredUsername || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Email</span>
            <span class="value">{{ user.email || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">User ID</span>
            <span class="value monospace">{{ user.sub }}</span>
          </div>
        </div>
      </div>

      <div class="roles-card">
        <h2>Roles & Permissions</h2>

        <div class="roles-list">
          <span
            v-for="role in user.roles"
            :key="role"
            class="role-badge"
            :class="getRoleBadgeClass(role)"
          >
            {{ role }}
          </span>
          <span v-if="user.roles.length === 0" class="no-roles">No roles assigned</span>
        </div>

        <div class="permissions">
          <div class="permission-item">
            <span class="permission-icon" :class="{ 'permission-granted': isAdmin }">
              {{ isAdmin ? '✓' : '✗' }}
            </span>
            <span class="permission-label">Administrator access</span>
          </div>
          <div class="permission-item">
            <span class="permission-icon" :class="{ 'permission-granted': isDeveloper || isAdmin }">
              {{ (isDeveloper || isAdmin) ? '✓' : '✗' }}
            </span>
            <span class="permission-label">Create and manage builds</span>
          </div>
          <div class="permission-item">
            <span class="permission-icon" :class="{ 'permission-granted': isAdmin || hasOrganizations }">
              {{ (isAdmin || hasOrganizations) ? '✓' : '✗' }}
            </span>
            <span class="permission-label">View builds and logs (within your organizations)</span>
          </div>
        </div>

        <div class="organizations-section">
          <h3>Organizations</h3>
          <div v-if="isAdmin" class="admin-note">
            As an administrator, you have access to all organizations.
          </div>
          <div v-else-if="organizationSlugs.length > 0" class="organizations-list">
            <span
              v-for="slug in organizationSlugs"
              :key="slug"
              class="org-badge"
            >
              {{ slug }}
            </span>
          </div>
          <div v-else class="no-organizations">
            You are not a member of any organization.
          </div>
        </div>
      </div>

      <!-- Personal Access Tokens Section -->
      <div class="tokens-card">
        <div class="tokens-header">
          <h2>Personal Access Tokens</h2>
          <button
            v-if="!showCreateForm"
            class="btn-create"
            @click="showCreateForm = true"
          >
            + New Token
          </button>
        </div>

        <p class="tokens-description">
          Personal access tokens can be used for CLI authentication or API access.
          Tokens inherit your current permissions.
        </p>

        <!-- New Token Display -->
        <div v-if="tokensStore.newlyCreatedToken" class="new-token-banner">
          <div class="new-token-header">
            <strong>Your new token</strong>
            <span class="new-token-warning">Make sure to copy it now. You won't be able to see it again!</span>
          </div>
          <div class="new-token-value">
            <code>{{ tokensStore.newlyCreatedToken }}</code>
            <button
              class="btn-copy"
              @click="copyToClipboard(tokensStore.newlyCreatedToken!)"
              title="Copy to clipboard"
            >
              Copy
            </button>
          </div>
          <button class="btn-dismiss" @click="dismissNewToken">
            I've copied my token
          </button>
        </div>

        <!-- Create Token Form -->
        <div v-else-if="showCreateForm" class="create-form">
          <div class="form-group">
            <label for="token-name">Token name</label>
            <input
              id="token-name"
              v-model="tokenName"
              type="text"
              placeholder="e.g., CLI on MacBook"
              :disabled="tokensStore.loading"
            />
          </div>
          <div class="form-group">
            <label for="token-expiry">Expiration</label>
            <select id="token-expiry" v-model="tokenExpiry" :disabled="tokensStore.loading">
              <option v-for="opt in expiryOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </div>
          <div class="form-actions">
            <button class="btn-cancel" @click="showCreateForm = false" :disabled="tokensStore.loading">
              Cancel
            </button>
            <button
              class="btn-generate"
              @click="handleCreateToken"
              :disabled="!tokenName.trim() || tokensStore.loading"
            >
              {{ tokensStore.loading ? 'Creating...' : 'Generate token' }}
            </button>
          </div>
          <p v-if="tokensStore.error" class="form-error">{{ tokensStore.error }}</p>
        </div>

        <!-- Token List -->
        <div v-if="tokensStore.loading && !tokensStore.tokens.length" class="tokens-loading">
          Loading tokens...
        </div>

        <div v-else-if="activeTokens.length === 0 && !showCreateForm" class="no-tokens">
          <p>No personal access tokens yet.</p>
          <p>Create a token to use the CLI or access the API.</p>
        </div>

        <div v-else class="tokens-list">
          <div
            v-for="token in activeTokens"
            :key="token.id"
            class="token-item"
            :class="{ 'token-expired': isExpired(token.expiresAt) }"
          >
            <div class="token-info">
              <div class="token-name">
                {{ token.name }}
                <span v-if="isExpired(token.expiresAt)" class="token-status expired">Expired</span>
              </div>
              <div class="token-meta">
                <span class="token-prefix">{{ token.tokenPrefix }}...</span>
                <span class="token-separator">·</span>
                <span>Created {{ formatRelativeDay(token.createdAt) }}</span>
                <span class="token-separator">·</span>
                <span v-if="token.lastUsedAt">Last used {{ formatRelativeDay(token.lastUsedAt) }}</span>
                <span v-else>Never used</span>
                <template v-if="token.expiresAt">
                  <span class="token-separator">·</span>
                  <span :class="{ 'text-warning': isExpired(token.expiresAt) }">
                    {{ isExpired(token.expiresAt) ? 'Expired' : 'Expires' }} {{ formatDate(token.expiresAt) }}
                  </span>
                </template>
              </div>
            </div>
            <button
              class="btn-revoke"
              @click="handleRevokeToken(token.id)"
              :disabled="tokensStore.loading"
            >
              Revoke
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.profile-view {
  max-width: 800px;
  margin: 0 auto;
}

.header {
  margin-bottom: 24px;
}

h1 {
  font-size: 1.75rem;
  margin: 0;
  color: var(--color-text-primary);
}

h2 {
  font-size: 1.25rem;
  margin: 0 0 16px 0;
  color: var(--color-text-primary);
}

.loading {
  text-align: center;
  padding: 40px;
  color: var(--color-text-secondary);
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.profile-card {
  background: var(--color-card-bg);
  border-radius: 8px;
  box-shadow: 0 1px 3px var(--color-card-shadow);
  border: 1px solid var(--color-card-border);
  padding: 24px;
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--color-loading-gradient-start) 0%, var(--color-loading-gradient-end) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-initial {
  color: white;
  font-size: 2rem;
  font-weight: 600;
}

.profile-details {
  flex: 1;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid var(--color-border-primary);
}

.detail-row:last-child {
  border-bottom: none;
}

.label {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.value {
  font-weight: 500;
  color: var(--color-text-primary);
}

.value.monospace {
  font-family: monospace;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
}

.roles-card,
.tokens-card {
  background: var(--color-card-bg);
  border-radius: 8px;
  box-shadow: 0 1px 3px var(--color-card-shadow);
  border: 1px solid var(--color-card-border);
  padding: 24px;
}

.roles-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
}

.role-badge {
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: capitalize;
}

.role-admin {
  background: var(--color-warning-bg);
  color: var(--color-warning-text);
}

.role-developer {
  background: var(--color-info-bg);
  color: var(--color-info-text);
}

.role-readonly {
  background: var(--color-pending-bg);
  color: var(--color-pending-text);
}

.role-default {
  background: var(--color-pending-bg);
  color: var(--color-text-secondary);
}

.no-roles {
  color: var(--color-text-muted);
  font-style: italic;
}

.permissions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.permission-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.permission-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  background: var(--color-error-bg);
  color: var(--color-error-text);
}

.permission-icon.permission-granted {
  background: var(--color-success-bg);
  color: var(--color-success-text);
}

.permission-label {
  color: var(--color-text-primary);
}

.organizations-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--color-border-primary);
}

.organizations-section h3 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 12px 0;
}

.organizations-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.org-badge {
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 500;
  background: var(--color-accent-light);
  color: var(--color-accent);
}

.admin-note {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  font-style: italic;
}

.no-organizations {
  color: var(--color-text-muted);
  font-size: 0.9rem;
  font-style: italic;
}

/* Tokens Section Styles */
.tokens-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.tokens-header h2 {
  margin: 0;
}

.tokens-description {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin: 0 0 20px 0;
}

.btn-create {
  background: var(--color-header-bg);
  color: var(--color-header-text);
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-create:hover {
  opacity: 0.9;
}

/* New Token Banner */
.new-token-banner {
  background: var(--color-success-bg);
  border: 1px solid var(--color-success-border);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.new-token-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}

.new-token-header strong {
  color: var(--color-success-text);
}

.new-token-warning {
  color: var(--color-success-text);
  font-size: 0.85rem;
}

.new-token-value {
  display: flex;
  gap: 8px;
  align-items: center;
  background: var(--color-bg-secondary);
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 12px;
}

.new-token-value code {
  flex: 1;
  font-family: monospace;
  font-size: 0.9rem;
  word-break: break-all;
  color: var(--color-text-primary);
}

.btn-copy {
  background: var(--color-header-bg);
  color: var(--color-header-text);
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  flex-shrink: 0;
}

.btn-copy:hover {
  opacity: 0.9;
}

.btn-dismiss {
  background: var(--color-success-text);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
}

.btn-dismiss:hover {
  opacity: 0.9;
}

/* Create Form */
.create-form {
  background: var(--color-bg-tertiary);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: 6px;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-input-border);
  border-radius: 6px;
  font-size: 0.95rem;
  box-sizing: border-box;
  background: var(--color-input-bg);
  color: var(--color-input-text);
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--color-input-focus-border);
  box-shadow: 0 0 0 2px var(--color-input-focus-ring);
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-cancel {
  background: var(--color-btn-secondary-bg);
  color: var(--color-btn-secondary-text);
  border: 1px solid var(--color-btn-secondary-border);
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
}

.btn-cancel:hover:not(:disabled) {
  background: var(--color-btn-secondary-hover);
}

.btn-generate {
  background: var(--color-header-bg);
  color: var(--color-header-text);
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
}

.btn-generate:hover:not(:disabled) {
  opacity: 0.9;
}

.btn-generate:disabled,
.btn-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-error {
  color: var(--color-error-text);
  font-size: 0.85rem;
  margin-top: 12px;
}

/* Token List */
.tokens-loading {
  text-align: center;
  padding: 20px;
  color: var(--color-text-secondary);
}

.no-tokens {
  text-align: center;
  padding: 30px;
  color: var(--color-text-secondary);
}

.no-tokens p {
  margin: 4px 0;
}

.tokens-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.token-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: var(--color-bg-tertiary);
  border-radius: 8px;
}

.token-item.token-expired {
  opacity: 0.7;
}

.token-info {
  flex: 1;
  min-width: 0;
}

.token-name {
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.token-status.expired {
  font-size: 0.75rem;
  font-weight: normal;
  background: var(--color-error-bg);
  color: var(--color-error-text);
  padding: 2px 6px;
  border-radius: 4px;
}

.token-meta {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
}

.token-prefix {
  font-family: monospace;
  background: var(--color-badge-default-bg);
  padding: 2px 6px;
  border-radius: 4px;
}

.token-separator {
  margin: 0 6px;
  color: var(--color-text-muted);
}

.text-warning {
  color: var(--color-warning-text);
}

.btn-revoke {
  background: transparent;
  color: var(--color-btn-danger-bg);
  border: 1px solid var(--color-btn-danger-bg);
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  flex-shrink: 0;
}

.btn-revoke:hover:not(:disabled) {
  background: var(--color-error-bg);
}

.btn-revoke:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Tablet breakpoint (768px) */
@media (max-width: 768px) {
  .profile-card {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .detail-row {
    flex-direction: column;
    gap: 4px;
  }

  .label {
    font-size: 0.8rem;
  }

  .tokens-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .token-item {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .token-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .token-separator {
    display: none;
  }
}

/* Phone breakpoint (480px) */
@media (max-width: 480px) {
  .profile-view {
    padding: 0;
  }

  h1 {
    font-size: 1.5rem;
  }

  .profile-card,
  .roles-card,
  .tokens-card {
    border-radius: 0;
    margin-left: calc(-1 * var(--mobile-padding, 12px));
    margin-right: calc(-1 * var(--mobile-padding, 12px));
    width: calc(100% + 2 * var(--mobile-padding, 12px));
    padding: var(--mobile-padding, 12px);
  }

  .avatar {
    width: 64px;
    height: 64px;
  }

  .avatar-initial {
    font-size: 1.5rem;
  }

  .value.monospace {
    word-break: break-all;
    font-size: 0.8rem;
  }

  .btn-create {
    width: 100%;
    min-height: 44px;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-cancel,
  .btn-generate {
    width: 100%;
    min-height: 44px;
  }

  .new-token-value {
    flex-direction: column;
  }

  .new-token-value code {
    font-size: 0.8rem;
  }

  .btn-copy,
  .btn-dismiss {
    width: 100%;
    min-height: 44px;
  }

  .btn-revoke {
    width: 100%;
    min-height: 44px;
  }
}
</style>
